import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useParams, useNavigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Programs from './pages/Programs';
import Resources from './pages/Resources';
import Contact from './pages/Contact';
import BasinKulturSanat from './pages/BasinKulturSanat';
import KadininKalemi from './pages/KadininKalemi';
import ArticleDetail from './pages/ArticleDetail';
import ProgramDetail from './pages/ProgramDetail';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsOfUse from './pages/legal/TermsOfUse';
import CookiePolicy from './pages/legal/CookiePolicy';
import PdfViewer from './components/PdfViewer';
import { useLanguage } from './context/LanguageContext';
import { useTranslation } from 'react-i18next';

const LanguageSync: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { lang } = useParams<{ lang: string }>();
  const { language, setLanguage } = useLanguage();
  
  useEffect(() => {
    if (lang) {
      const clean = lang.toLowerCase().split(/[-_]/)[0];
      const normalized = (clean === 'ku' || clean === 'kmr') ? 'krm' : clean;
      if (['tr', 'en', 'ar', 'krm'].includes(normalized)) {
        if (normalized !== language) {
          setLanguage(normalized as any);
        }
      }
    }
  }, [lang, language, setLanguage]);

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const { t } = useTranslation();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const location = useLocation();

  const { language } = useLanguage();

  // Scroll to top on route or language change
  useEffect(() => {
    window.scrollTo(0, 0);
    // Safe fallback to make sure browser scrolls to top after DOM has updated
    const timeoutId = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [location.pathname, language]);

  return (
    <div className="flex flex-col min-h-screen bg-brand-neutral-bg selection:bg-brand-purple-200 selection:text-brand-purple-900">
      <Navbar setPdfUrl={setPdfUrl} />

      <main className="flex-grow">
        <Routes>
          <Route path="/" element={<Navigate to="/tr" replace />} />
          <Route path="/:lang" element={<LanguageSync><Home openPdf={setPdfUrl} /></LanguageSync>} />
          <Route path="/:lang/biz-kimiz" element={<LanguageSync><About /></LanguageSync>} />
          <Route path="/:lang/raporlar" element={<LanguageSync><Resources openPdf={setPdfUrl} /></LanguageSync>} />
          <Route path="/:lang/iletisim" element={<LanguageSync><Contact /></LanguageSync>} />
          <Route path="/:lang/programs" element={<LanguageSync><Programs /></LanguageSync>} />
          <Route path="/:lang/kalem" element={<LanguageSync><KadininKalemi /></LanguageSync>} />
          <Route path="/:lang/kadinin-kalemi" element={<LanguageSync><KadininKalemi /></LanguageSync>} />
          <Route path="/:lang/kose-yazilari" element={<LanguageSync><KadininKalemi /></LanguageSync>} />
          
          <Route path="/:lang/basin-bultenleri" element={<LanguageSync><BasinKulturSanat currentPage="basin-bultenleri" /></LanguageSync>} />
          <Route path="/:lang/etkinlikler" element={<LanguageSync><BasinKulturSanat currentPage="etkinlikler" /></LanguageSync>} />
          <Route path="/:lang/galeri" element={<LanguageSync><BasinKulturSanat currentPage="galeri" /></LanguageSync>} />
          <Route path="/:lang/kampanya" element={<LanguageSync><BasinKulturSanat currentPage="kampanyalar" /></LanguageSync>} />
          <Route path="/:lang/kampanyalar" element={<LanguageSync><BasinKulturSanat currentPage="kampanyalar" /></LanguageSync>} />
          <Route path="/:lang/basin" element={<LanguageSync><BasinKulturSanat currentPage="basin" /></LanguageSync>} />
          <Route path="/:lang/haberler" element={<LanguageSync><BasinKulturSanat currentPage="basin" /></LanguageSync>} />
          <Route path="/:lang/programlar" element={<LanguageSync><Programs /></LanguageSync>} />

          <Route path="/:lang/haberler/:slug" element={<LanguageSync><ArticleDetailWrapper openPdf={setPdfUrl} /></LanguageSync>} />
          <Route path="/:lang/programlar/:slug" element={<LanguageSync><ProgramDetailWrapper /></LanguageSync>} />

          {/* Legal Pages */}
          <Route path="/:lang/gizlilik-politikasi" element={<LanguageSync><PrivacyPolicy /></LanguageSync>} />
          <Route path="/:lang/kullanim-sartlari" element={<LanguageSync><TermsOfUse /></LanguageSync>} />
          <Route path="/:lang/cerez-politikasi" element={<LanguageSync><CookiePolicy /></LanguageSync>} />
          <Route path="/gizlilik-politikasi" element={<Navigate to="/tr/gizlilik-politikasi" replace />} />
          <Route path="/kullanim-sartlari" element={<Navigate to="/tr/kullanim-sartlari" replace />} />
          <Route path="/cerez-politikasi" element={<Navigate to="/tr/cerez-politikasi" replace />} />

          {/* Fallback for unknown routes inside a lang to go to that lang's home instead of forcing TR */}
          <Route path="/:lang/*" element={<LanguageSync><Home openPdf={setPdfUrl} /></LanguageSync>} />
          <Route path="*" element={<Navigate to="/tr" replace />} />
        </Routes>
      </main>

      <Footer />

      {/* PDF Modal Viewer */}
      {pdfUrl && (
        <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-md flex items-center justify-center p-4 md:p-10">
          <div className="bg-white w-full h-full max-w-6xl max-h-[90vh] rounded-[2rem] overflow-hidden flex flex-col shadow-2xl relative animate-in zoom-in-95 duration-300">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white shadow-sm z-10">
              <h3 className="font-serif font-bold text-brand-purple-900 text-lg">{t('resources.view_modal_title')}</h3>
              <button onClick={() => setPdfUrl(null)} className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-brand-purple-100 hover:text-brand-purple-900 transition-colors">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <PdfViewer url={pdfUrl} />
          </div>
        </div>
      )}
    </div>
  );
};

const ArticleDetailWrapper: React.FC<{ openPdf: (url: string) => void }> = ({ openPdf }) => {
  const { slug, lang } = useParams<{ slug: string, lang: string }>();
  if (!slug) return <Navigate to={`/${lang || 'tr'}/haberler`} replace />;
  return <ArticleDetail slug={slug} openPdf={openPdf} />;
};

const ProgramDetailWrapper: React.FC = () => {
  const { slug, lang } = useParams<{ slug: string, lang: string }>();
  if (!slug) return <Navigate to={`/${lang || 'tr'}/programlar`} replace />;
  return <ProgramDetail slug={slug} />;
};

const App: React.FC = () => {
  return <AppContent />;
};

export default App;
